import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Authentication.css";
import logo from "../logo.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";

import { auth } from "../firebase";
import { useAuth } from "../context/AuthContext"; // ✅ ADDED: read currentUser + loading

const Authentication = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loadingLocal, setLoadingLocal] = useState(false); // ✅ CHANGED: avoid conflict with AuthContext loading
  const [error, setError] = useState("");
  const [mode, setMode] = useState("signup"); // login | signup | reset
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();

  const { currentUser, loading } = useAuth(); // ✅ ADDED: auth state from context

  // ✅ ADDED: after user logs in, check if they selected a plan before auth
  useEffect(() => {
    if (loading) return;       // AuthContext still loading
    if (!currentUser) return;  // Not logged in yet

    const pendingPlan = localStorage.getItem("kairos_pending_plan");

    // If user came from pricing selection, take them to checkout
    if (pendingPlan) {
      localStorage.removeItem("kairos_pending_plan");
      navigate(`/checkout?plan=${encodeURIComponent(pendingPlan)}`, { replace: true });
      return;
    }

    // Otherwise do NOTHING here:
    // ✅ Your AuthContext already decides /choose-plan vs /workspaces
  }, [currentUser, loading, navigate]);

  const handleOAuth = async (provider) => {
    try {
      setLoadingLocal(true);
      setError("");
      await signInWithPopup(auth, provider);

      // ✅ REMOVED: navigate("/workspaces")
      // Reason: AuthContext + useEffect above will redirect correctly.
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingLocal(false);
    }
  };

  const handleEmailAuth = async () => {
    if (password.length < 6 && mode !== "reset") {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (mode === "signup" && !name.trim()) {
      setError("Please enter your name.");
      return;
    }

    try {
      setLoadingLocal(true);
      setError("");
      setMessage("");

      if (mode === "login") {
        await signInWithEmailAndPassword(auth, email, password);

        // ✅ REMOVED: navigate("/workspaces")
        // Reason: AuthContext + useEffect above will redirect correctly.
      }

      if (mode === "signup") {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        await updateProfile(userCredential.user, {
          displayName: name,
        });

        // ✅ REMOVED: navigate("/workspaces")
        // Reason: new users should be redirected to /choose-plan (or /checkout if pendingPlan exists)
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingLocal(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      setLoadingLocal(true);
      setError("");
      setMessage("");
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset email sent.");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingLocal(false);
    }
  };

  return (
    <main className="home">
      <section id="home" className="hero">
        <div className="hero-content">
          <div>
            <h1>
              <span className="gradient-text">Creating</span> an{" "}
              <span className="gradient-text">Account</span>
            </h1>
            <h2>
              <span className="gradient-text">Choose</span> a{" "}
              <span className="gradient-text">Method</span>
            </h2>
          </div>

          <div className="auth-box">
            {mode !== "reset" && (
              <>
                <button
                  className="auth google"
                  onClick={() => handleOAuth(googleProvider)}
                  disabled={loadingLocal}
                >
                  Continue with Google
                </button>

                <div className="divider">or</div>
              </>
            )}

            {mode !== "reset" && mode === "signup" && (
              <input
                type="text"
                placeholder="Display Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}

            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {mode !== "reset" && (
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            )}

            {mode === "reset" ? (
              <button
                className="auth email"
                onClick={handlePasswordReset}
                disabled={loadingLocal || !email}
              >
                Send reset link
              </button>
            ) : (
              <button
                className="auth email"
                onClick={handleEmailAuth}
                disabled={loadingLocal || !email || !password}
              >
                {mode === "login" ? "Continue with Email" : "Create Account"}
              </button>
            )}

            {error && <p className="error">{error}</p>}
            {message && <p className="success">{message}</p>}

            <div className="auth-links">
              {mode === "login" && (
                <>
                  <button onClick={() => setMode("reset")}>Forgot password?</button>
                  <button onClick={() => setMode("signup")}>Create an account</button>
                </>
              )}

              {mode === "signup" && (
                <button onClick={() => setMode("login")}>Already have an account?</button>
              )}

              {mode === "reset" && <button onClick={() => setMode("login")}>Back to login</button>}
            </div>

            <p>By Logging In or Creating an account you agree to the below policies</p>

            <div className="legal-links">
              <button className="legal-link-btn" onClick={() => navigate("/privacy-policy")} type="button">
                Privacy Policy
              </button>

              <button className="legal-link-btn" onClick={() => navigate("/terms-and-conditions")} type="button">
                Terms & Conditions
              </button>

              <button className="legal-link-btn" onClick={() => navigate("/cookie-policy")} type="button">
                Cookie Policy
              </button>

              <button className="legal-link-btn" onClick={() => navigate("/refund-policy")} type="button">
                Refund Policy
              </button>

              <button className="legal-link-btn" onClick={() => navigate("/acceptable-use-policy")} type="button">
                Acceptable Use
              </button>

              <button className="legal-link-btn" onClick={() => navigate("/ai-use-policy")} type="button">
                AI Use & Content Policy
              </button>

              <button className="legal-link-btn" onClick={() => navigate("/subscription-policy")} type="button">
                Subscription Policy
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <img
          onClick={() => {
            document.getElementById("top")?.scrollIntoView({ behavior: "smooth" });
          }}
          src={logo}
          alt="Kairos Logo"
          className="footer-logo"
        />
        <p>© 2026 Kairos AI. All rights reserved.</p>
        <p>Kairos Policies</p>
      </footer>
    </main>
  );
};

export default Authentication;
